import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { useRequiredParams } from '@util'
import type { RecordingId } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Divider, Group, Stack, Text } from '@mantine/core'
import { useToastError } from '@hooks'
import { formatDate, useRecordingPlayback } from '@front/model'
import { OptionalRow, Page, RecordingConnectionTime, TButton, TStack } from '@components'

type MetaEntry = {
  // seconds from the start of the recording
  start: number
  end: number
}

function Recording2Page(): ReactElement {
  const { recordingId } = useRequiredParams<{ recordingId: RecordingId }>()
  const { data, isLoading, error } = useRecordingPlayback({ recordingId })
  useToastError(error)

  const metadataUrl = data?.metadata

  const {
    data: metadata,
    isLoading: metadataLoading,
    error: metaError,
  } = useQuery({
    enabled: Boolean(metadataUrl),
    queryKey: ['recording-meta', recordingId],
    queryFn: async () => {
      if (!metadataUrl) return
      const response = await fetch(metadataUrl)
      if (!response.ok) {
        throw new Error('Failed to fetch metadata')
      }
      const metadata = (await response.json()) as MetaEntry[]
      return metadata
    },
  })
  useToastError(metaError?.message)

  const audioRef = useRef<HTMLAudioElement>(null)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const metaEntries = useMemo(() => normalizeMetaEntries(metadata ?? [], duration), [metadata, duration])
  const timelineDuration = useMemo(() => getTimelineDuration(metaEntries, duration), [duration, metaEntries])
  const timelineSegments = useMemo(
    () => buildTimelineSegments(metaEntries, timelineDuration),
    [metaEntries, timelineDuration]
  )

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleLoadedMetadata = (): void => {
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0)
    }
    const handleTimeUpdate = (): void => setCurrentTime(audio.currentTime)

    handleLoadedMetadata()
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('durationchange', handleLoadedMetadata)
    audio.addEventListener('timeupdate', handleTimeUpdate)

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('durationchange', handleLoadedMetadata)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
    }
  }, [data?.audio])

  const seekTo = (seconds: number): void => {
    const audio = audioRef.current
    if (!audio) return

    const clampedSeconds = clamp(seconds, 0, timelineDuration)
    audio.currentTime = clampedSeconds
    setCurrentTime(clampedSeconds)
  }

  const skipToPreviousMeta = (): void => {
    const previous = [...metaEntries].reverse().find((entry) => entry.start < currentTime - 0.25) ?? metaEntries[0]
    if (!previous) return
    seekTo(previous.start)
  }

  const skipToNextMeta = (): void => {
    const next = metaEntries.find((entry) => entry.start > currentTime + 0.25) ?? metaEntries.at(-1)
    if (!next) return
    seekTo(next.start)
  }

  return (
    <Page>
      <Page.Heading back goBackBehavior='history' title={recordingId} />
      <Page.Content scrollArea>
        <Stack>
          {isLoading && <div>Crunching the latest data just for you...</div>}
          {data && (
            <>
              <TStack divider={<Divider />}>
                <OptionalRow label='Started' value={data.startedAt ? formatDate(data.startedAt) : undefined} />
                <OptionalRow
                  label='Connected'
                  value={<RecordingConnectionTime connectedAt={data.connectedAt} noAudioSince={data.noAudioSince} />}
                />
              </TStack>
              <div className='min-w-0'>
                <audio ref={audioRef} controls src={data.audio} className='w-full' />
              </div>
            </>
          )}
          {metadataLoading && <Text c='dimmed'>Loading metadata...</Text>}
          <PlaybackTimeline
            currentTime={currentTime}
            duration={timelineDuration}
            metadataCount={metaEntries.length}
            segments={timelineSegments}
            onSeek={seekTo}
            onPrevious={skipToPreviousMeta}
            onNext={skipToNextMeta}
          />
        </Stack>
      </Page.Content>
    </Page>
  )
}

type TimelineSegment = MetaEntry & {
  type: 'meta' | 'gap'
}

type PlaybackTimelineProps = {
  currentTime: number
  duration: number
  metadataCount: number
  segments: TimelineSegment[]
  onSeek: (seconds: number) => void
  onPrevious: () => void
  onNext: () => void
}

function PlaybackTimeline({
  currentTime,
  duration,
  metadataCount,
  segments,
  onSeek,
  onPrevious,
  onNext,
}: PlaybackTimelineProps): ReactElement {
  const progress = duration > 0 ? clamp((currentTime / duration) * 100, 0, 100) : 0
  const hasMetadata = metadataCount > 0

  return (
    <Stack gap='xs' className='w-full min-w-0'>
      <Group justify='space-between' wrap='nowrap'>
        <Group gap='xs' wrap='nowrap'>
          <TButton
            icon='skip_previous'
            tooltip='Previous meta entry'
            variant='ghost'
            disabled={!hasMetadata}
            onClick={onPrevious}
          />
          <TButton
            icon='skip_next'
            tooltip='Next meta entry'
            variant='ghost'
            disabled={!hasMetadata}
            onClick={onNext}
          />
        </Group>
        <Text size='xs' c='dimmed'>
          {formatTime(currentTime)} / {formatTime(duration)}
        </Text>
      </Group>
      <div
        className='relative h-6 w-full cursor-pointer overflow-hidden rounded-sm'
        role='slider'
        aria-label='Playback timeline'
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(currentTime)}
        style={{ backgroundColor: 'var(--mantine-color-gray-4)' }}
        onClick={(event) => {
          if (duration <= 0) return
          const rect = event.currentTarget.getBoundingClientRect()
          onSeek(((event.clientX - rect.left) / rect.width) * duration)
        }}
      >
        {duration > 0 &&
          segments.map((segment, index) => (
            <div
              key={`${segment.type}-${segment.start}-${segment.end}-${index}`}
              className='absolute top-0 h-full'
              style={{
                backgroundColor:
                  segment.type === 'meta' ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-gray-5)',
                left: `${(segment.start / duration) * 100}%`,
                width: `${((segment.end - segment.start) / duration) * 100}%`,
              }}
              title={`${segment.type === 'meta' ? 'Meta' : 'No meta'} ${formatTime(segment.start)}-${formatTime(segment.end)}`}
            />
          ))}
        {duration > 0 && (
          <div
            className='absolute top-0 h-full w-0.5 shadow-sm'
            style={{ left: `${progress}%`, backgroundColor: 'var(--mantine-color-dark-9)' }}
            aria-hidden='true'
          />
        )}
      </div>
    </Stack>
  )
}

function normalizeMetaEntries(entries: MetaEntry[], duration: number): MetaEntry[] {
  const normalized = entries
    .map((entry) => ({
      start: clamp(Number(entry.start), 0, duration || Number.MAX_SAFE_INTEGER),
      end: clamp(Number(entry.end), 0, duration || Number.MAX_SAFE_INTEGER),
    }))
    .filter((entry) => Number.isFinite(entry.start) && Number.isFinite(entry.end) && entry.end > entry.start)
    .sort((a, b) => a.start - b.start)

  return normalized.reduce<MetaEntry[]>((merged, entry) => {
    const previous = merged.at(-1)
    if (!previous || entry.start > previous.end) {
      merged.push(entry)
      return merged
    }

    previous.end = Math.max(previous.end, entry.end)
    return merged
  }, [])
}

function buildTimelineSegments(metaEntries: MetaEntry[], duration: number): TimelineSegment[] {
  if (duration <= 0) return []

  const segments: TimelineSegment[] = []
  let cursor = 0
  for (const entry of metaEntries) {
    if (entry.start > cursor) segments.push({ type: 'gap', start: cursor, end: entry.start })
    segments.push({ type: 'meta', ...entry })
    cursor = entry.end
  }

  if (cursor < duration) segments.push({ type: 'gap', start: cursor, end: duration })
  return segments
}

function getTimelineDuration(metaEntries: MetaEntry[], duration: number): number {
  if (duration > 0) return duration
  return metaEntries.at(-1)?.end ?? 0
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds)) return '0:00'
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

export const Route = createFileRoute('/recordings_/$recordingId/')({
  component: Recording2Page,
})
